/**
 * Scelta dei partecipanti di un gruppo: Lead/Clienti con telefono (ricerca che rispetta
 * le restrizioni di visibilita', searchClientsForChat) oppure un numero digitato.
 * Usato da "Aggiungi partecipanti" e da "Nuovo gruppo".
 */
import { useEffect, useMemo, useState } from 'react';
import { Search, X, Phone, User, Loader2 } from 'lucide-react';
import { useUserData } from '@/lib/auth';
import { classifyNewChatQuery } from '@/lib/services/whatsapp/newChatLogic';
import { searchClientsForChat, type ClientChatCandidate } from '@/lib/services/whatsapp/clientPhoneSearch';
import { formatPhoneForDisplay } from '@/lib/services/whatsapp/chatListRules';
import { iconTip } from '@/components/messages/ChatTooltip';

export interface PickedPerson {
  digits: string;
  label: string;
}

interface Props {
  selected: PickedPerson[];
  onChange: (next: PickedPerson[]) => void;
  /** Numeri gia' nel gruppo: non si propongono. */
  excludeDigits?: string[];
  disabled?: boolean;
}

export function ParticipantPicker({ selected, onChange, excludeDigits = [], disabled }: Props) {
  const { organizationId, userProfile } = useUserData();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ClientChatCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);
  const parsed = useMemo(() => classifyNewChatQuery(query), [query]);
  const taken = useMemo(() => new Set([...excludeDigits, ...selected.map((s) => s.digits)]), [excludeDigits, selected]);

  useEffect(() => {
    if (parsed.kind !== 'name' || parsed.term.length < 2 || !organizationId) {
      setResults([]);
      setSearching(false);
      return;
    }
    let stop = false;
    setSearching(true);
    setFailed(false);
    const t = setTimeout(() => {
      searchClientsForChat(organizationId, userProfile?.id ?? null, parsed.term)
        .then((rows) => { if (!stop) setResults(rows); })
        .catch(() => { if (!stop) { setResults([]); setFailed(true); } })
        .finally(() => { if (!stop) setSearching(false); });
    }, 300);
    return () => { stop = true; clearTimeout(t); };
  }, [parsed, organizationId, userProfile?.id]);

  const add = (p: PickedPerson) => {
    if (taken.has(p.digits)) return;
    onChange([...selected, p]);
    setQuery('');
  };
  const row = 'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/5 disabled:opacity-50';

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((s) => (
            <span key={s.digits} className="inline-flex items-center gap-1 rounded-full bg-[rgba(35,131,226,0.1)] px-2 py-0.5 text-xs text-[#2383E2]">
              {s.label}
              <button type="button" disabled={disabled} onClick={() => onChange(selected.filter((x) => x.digits !== s.digits))}
                className="rounded-full hover:bg-[rgba(35,131,226,0.2)]" {...iconTip(`Togli ${s.label}`)}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#787774]" />
        <input type="text" value={query} disabled={disabled} onChange={(e) => setQuery(e.target.value)}
          placeholder="Nome del Lead o Cliente, oppure un numero"
          aria-label="Cerca un Lead o Cliente, oppure scrivi un numero"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && parsed.kind === 'phone') { e.preventDefault(); add({ digits: parsed.digits, label: formatPhoneForDisplay(parsed.digits) }); }
          }}
          className="w-full h-8 pl-8 pr-2 rounded-md border border-[rgba(55,53,47,0.16)] dark:border-white/10 bg-white dark:bg-white/5 text-sm text-[#37352F] dark:text-white placeholder:text-[#787774] focus:outline-none focus:border-[#2383E2]" />
      </div>
      <div className="max-h-44 overflow-y-auto">
        {parsed.kind === 'phone' && (
          <button type="button" className={row} disabled={disabled || taken.has(parsed.digits)}
            onClick={() => add({ digits: parsed.digits, label: formatPhoneForDisplay(parsed.digits) })}>
            <Phone className="w-4 h-4 text-emerald-600" />
            <span>{taken.has(parsed.digits) ? 'Già inserito: ' : 'Aggiungi '}<strong>{formatPhoneForDisplay(parsed.digits)}</strong></span>
          </button>
        )}
        {parsed.kind === 'name' && searching && (
          <p className="flex items-center gap-2 px-2 py-1.5 text-xs text-[#787774]"><Loader2 className="w-3.5 h-3.5 animate-spin" />Cerco fra Lead e Clienti...</p>
        )}
        {parsed.kind === 'name' && !searching && failed && <p className="px-2 py-1.5 text-xs text-red-600">Ricerca non riuscita. Riprova tra poco.</p>}
        {parsed.kind === 'name' && !searching && !failed && parsed.term.length >= 2 && results.length === 0 && (
          <p className="px-2 py-1.5 text-xs text-[#787774]">Nessun Lead o Cliente trovato. Puoi scrivere direttamente il numero.</p>
        )}
        {parsed.kind === 'name' && !searching && results.map((c) => (
          c.numbers.length === 0 ? (
            <p key={c.id} className="px-2 py-1.5 text-xs text-[#787774]">{c.name}: {c.kind} senza numero salvato</p>
          ) : c.numbers.map((num) => (
            <button key={`${c.id}-${num}`} type="button" className={row} disabled={disabled || taken.has(num)}
              onClick={() => add({ digits: num, label: c.name })}>
              <User className="w-4 h-4 text-[#2383E2] flex-shrink-0" />
              <span className="min-w-0 flex-1 truncate">{c.name}
                <span className="text-xs text-[#787774]"> · {c.kind} · {formatPhoneForDisplay(num)}{taken.has(num) ? ' · già inserito' : ''}</span>
              </span>
            </button>
          ))
        ))}
      </div>
    </div>
  );
}
